import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import SlidingMenu from '../components/SlidingMenu';
import VersionFooter from '../components/VersionFooter';
import { clog, cerror } from '../shared/consoleLogger';
import QRCodeDisplay from '../components/luggage/QRCodeDisplay';

export default function LuggageTags() {
  const { t } = useTranslation();
  const [qrItems, setQrItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRItem, setSelectedQRItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTagForPrint, setSelectedTagForPrint] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    name: '',
    email: '',
    phone: '',
    headerText: "I'm Lost",
    footerText: 'Scan Me',
    format: 'simple',
    includeWhatsApp: true
  });

  // Fetch QR items when component mounts
  useEffect(() => {
    fetchQRItems();
  }, []);

  const fetchQRItems = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiCall("/api/qrItems?action=" + ApiActions.GET_QR_ITEMS, {}, 'POST');
      
      if (response.success) {
        setQrItems(response.qrItems || []);
      } else {
        setError(response.message || t('luggageTags.failedToLoad'));
      }
    } catch (err) {
      cerror('Error fetching QR items:', err);
      setError('An error occurred while fetching your QR tags.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditQR = (item) => {
    // Pre-fill form with item data
    setFormData({
      displayName: item.displayName || '',
      name: item.name || '',
      email: item.email || '',
      phone: item.phone || '',
      headerText: item.headerText || "I'm Lost",
      footerText: item.footerText || 'Scan Me',
      format: item.format || 'simple',
      includeWhatsApp: item.includeWhatsApp !== undefined ? item.includeWhatsApp : true
    });
    setIsEditMode(true);
    setSelectedQRItem(item); // Store item for update
    setShowFormModal(true);
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      let response;
      if (isEditMode && selectedQRItem) {
        // Update existing item
        response = await apiCall(
          "/api/qrItems?action=" + ApiActions.UPDATE_QR_ITEM,
          {
            id: selectedQRItem.id,
            ...formData
          },
          'POST'
        );

        if (response.success) {
          // Update item in list
          setQrItems(qrItems.map(item => 
            item.id === selectedQRItem.id ? response.qrItem : item
          ));
        }
      } else {
        // Create new item
        response = await apiCall(
          "/api/qrItems?action=" + ApiActions.CREATE_QR_ITEM,
          formData,
          'POST'
        );

        if (response.success) {
          // Add new item to list
          setQrItems([response.qrItem, ...qrItems]);
        }
      }

      if (response.success) {
        // Reset form and close modal
        setFormData({ displayName: '', name: '', email: '', phone: '', headerText: "I'm Lost", footerText: 'Scan Me', format: 'simple', includeWhatsApp: true });
        setShowFormModal(false);
        setIsEditMode(false);
        setSelectedQRItem(null);
      } else {
        setError(response.message || (isEditMode ? t('luggageTags.failedToUpdate') : t('luggageTags.failedToCreate')));
      }
    } catch (err) {
      cerror(`Error ${isEditMode ? 'updating' : 'creating'} QR item:`, err);
      setError(isEditMode ? t('luggageTags.failedToUpdate') : t('luggageTags.failedToCreate'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewQR = (item) => {
    setSelectedQRItem(item);
    setShowQRModal(true);
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setIsDeleting(true);
    
    try {
      const response = await apiCall(
        "/api/qrItems?action=" + ApiActions.DELETE_QR_ITEM,
        { id: itemToDelete.id },
        'POST'
      );
      
      if (response.success) {
        // Remove deleted item from list
        const updatedItems = qrItems.filter(item => item.id !== itemToDelete.id);
        setQrItems(updatedItems);
        // Clear selection if deleted item was selected
        if (selectedTagForPrint === itemToDelete.id) {
          setSelectedTagForPrint(null);
        }
        setShowDeleteModal(false);
        setItemToDelete(null);
      } else {
        setError(response.message || t('luggageTags.failedToDelete'));
      }
    } catch (err) {
      cerror('Error deleting QR item:', err);
      setError('An error occurred while deleting the QR tag.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="luggage-tags-page">
      <SlidingMenu title={t('luggageTags.title')} />
      
      <Container className="py-4">
        <Row>
          <Col>
            <div className="mb-4">
              <div className="d-flex justify-content-center gap-2 mb-3">
                {qrItems.length > 0 && (
                  <Button 
                    variant="outline-primary" 
                    onClick={() => {
                      if (selectedTagForPrint) {
                        window.open(`/luggage-tags/print?ids=${selectedTagForPrint}`, '_blank');
                      } else {
                        setError(t('luggageTags.pleaseSelectTag'));
                      }
                    }}
                  >
                    <i className="bi bi-printer"></i>
                    <span className="d-none d-sm-inline ms-2">{t('luggageTags.printSelectedTag')}</span>
                  </Button>
                )}
                <Button 
                  variant="primary" 
                  onClick={() => {
                    setIsEditMode(false);
                    setFormData({ displayName: '', name: '', email: '', phone: '', headerText: "I'm Lost", footerText: 'Scan Me', format: 'simple', includeWhatsApp: true });
                    setShowFormModal(true);
                  }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  {t('luggageTags.createNewTag')}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {isLoading ? (
              <div className="text-center py-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">{t('common.loading')}</span>
                </Spinner>
                <p className="mt-2">{t('luggageTags.loadingTags')}</p>
              </div>
            ) : qrItems.length === 0 ? (
              <Card>
                <Card.Body className="text-center py-5">
                  <i className="bi bi-qr-code" style={{ fontSize: '48px', color: '#ccc' }}></i>
                  <h5 className="mt-3">{t('luggageTags.noTagsYet')}</h5>
                  <p className="text-muted">{t('luggageTags.createFirstTag')}</p>
                  <Button variant="primary" onClick={() => {
                    setIsEditMode(false);
                    setFormData({ displayName: '', name: '', email: '', phone: '', headerText: "I'm Lost", footerText: 'Scan Me', format: 'simple', includeWhatsApp: true });
                    setShowFormModal(true);
                  }}>
                    {t('luggageTags.createFirstTagButton')}
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <Row>
                {qrItems.map((item) => (
                  <Col key={item.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                    <Card className={selectedTagForPrint === item.id ? 'border-primary border-2' : ''}>
                      <Card.Body>
                        <div className="d-flex align-items-center mb-2">
                          <Form.Check
                            type="radio"
                            name="printTagSelection"
                            id={`print-tag-${item.id}`}
                            checked={selectedTagForPrint === item.id}
                            onChange={() => setSelectedTagForPrint(item.id)}
                            label={<small>{t('luggageTags.selectForPrint')}</small>}
                            className="mb-0"
                          />
                        </div>
                        <div className="text-center mb-3">
                          <QRCodeDisplay
                            name={item.name}
                            email={item.email}
                            phone={item.phone}
                            profileLink={item.profileLink}
                            headerText={item.headerText || "I'm Lost"}
                            footerText={item.footerText || 'Scan Me'}
                            format={item.format || 'simple'}
                            size={150}
                            displayName={item.displayName}
                            showDisplayInfo={true}
                            includeWhatsApp={item.includeWhatsApp !== undefined ? item.includeWhatsApp : true}
                          />
                        </div>
                        <div className="text-center">
                          <h6 className="mb-1">{item.name}</h6>
                          <small className="text-muted d-block mb-2">{item.email}</small>
                          <div className="d-flex gap-2 justify-content-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleViewQR(item)}
                            >
                              <i className="bi bi-eye me-1"></i>
                              {t('common.view')}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleEditQR(item)}
                            >
                              <i className="bi bi-pencil me-1"></i>
                              {t('common.edit')}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => confirmDelete(item)}
                            >
                              <i className="bi bi-trash me-1"></i>
                              {t('common.delete')}
                            </Button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>

      {/* Create/Edit Modal */}
      <Modal 
        show={showFormModal} 
        onHide={() => {
          setShowFormModal(false);
          setIsEditMode(false);
          setSelectedQRItem(null);
          setFormData({ displayName: '', name: '', email: '', phone: '', headerText: "I'm Lost", footerText: 'Scan Me', format: 'simple', includeWhatsApp: true });
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? t('luggageTags.editQRTag') : t('luggageTags.createNewQRTag')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitForm}>
          <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.displayName')}</Form.Label>
              <Form.Control
                type="text"
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                placeholder={t('luggageTags.displayNamePlaceholder')}
              />
              <Form.Text className="text-muted">
                {t('luggageTags.displayNameHelp')}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.nameRequired')} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder={t('luggageTags.namePlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.emailRequired')} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder={t('luggageTags.emailPlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.phoneRequired')} <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder={t('luggageTags.phonePlaceholder')}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.headerText')}</Form.Label>
              <Form.Control
                type="text"
                name="headerText"
                value={formData.headerText}
                onChange={handleInputChange}
                placeholder={t('luggageTags.headerTextPlaceholder')}
              />
              <Form.Text className="text-muted">
                {t('luggageTags.headerTextHelp')}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.footerText')}</Form.Label>
              <Form.Control
                type="text"
                name="footerText"
                value={formData.footerText}
                onChange={handleInputChange}
                placeholder={t('luggageTags.footerTextPlaceholder')}
              />
              <Form.Text className="text-muted">
                {t('luggageTags.footerTextHelp')}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('luggageTags.qrCodeFormat')}</Form.Label>
              <Form.Select
                name="format"
                value={formData.format}
                onChange={handleInputChange}
              >
                <option value="simple">{t('luggageTags.formatSimple')}</option>
                <option value="vcard">{t('luggageTags.formatVcard')}</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {formData.format === 'vcard' 
                  ? t('luggageTags.formatVcardHelp')
                  : t('luggageTags.formatSimpleHelp')}
              </Form.Text>
            </Form.Group>
            {formData.format === 'vcard' && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="includeWhatsApp"
                  name="includeWhatsApp"
                  label={t('luggageTags.includeWhatsApp')}
                  checked={formData.includeWhatsApp}
                  onChange={(e) => setFormData({ ...formData, includeWhatsApp: e.target.checked })}
                />
                <Form.Text className="text-muted">
                  {t('luggageTags.includeWhatsAppHelp')}
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowFormModal(false);
              setIsEditMode(false);
              setSelectedQRItem(null);
              setFormData({ displayName: '', name: '', email: '', phone: '', headerText: "I'm Lost", footerText: 'Scan Me', format: 'simple', includeWhatsApp: true });
            }}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {isEditMode ? t('luggageTags.updating') : t('luggageTags.creating')}
                </>
              ) : (
                isEditMode ? t('luggageTags.updateTag') : t('luggageTags.createTag')
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* View QR Modal */}
      <Modal 
        show={showQRModal} 
        onHide={() => setShowQRModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('luggageTags.viewQRCode')} - {selectedQRItem?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedQRItem && (
            <QRCodeDisplay
              name={selectedQRItem.name}
              email={selectedQRItem.email}
              phone={selectedQRItem.phone}
              profileLink={selectedQRItem.profileLink}
              headerText={selectedQRItem.headerText || "I'm Lost"}
              footerText={selectedQRItem.footerText || 'Scan Me'}
              format={selectedQRItem.format || 'simple'}
              size={300}
              displayName={selectedQRItem.displayName}
              showDisplayInfo={true}
              includeWhatsApp={selectedQRItem.includeWhatsApp !== undefined ? selectedQRItem.includeWhatsApp : true}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQRModal(false)}>
            {t('common.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        show={showDeleteModal} 
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('luggageTags.deleteQRTag')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('luggageTags.deleteConfirm')} <strong>{itemToDelete?.name}</strong>{t('luggageTags.deleteConfirmEnd')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('common.loading')}
              </>
            ) : (
              t('common.delete')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* QR Code Image from Homepage */}
      <Container className="py-4">
        <Row>
          <Col className="text-center">
            <img 
              src="/images/qr-tag1.png" 
              alt="QR Code Luggage Tag"
              className="img-fluid"
              style={{ maxWidth: '300px', width: '100%', height: 'auto' }}
            />
          </Col>
        </Row>
      </Container>

      <VersionFooter />
    </div>
  );
}
